// src/utils/knowYourNumbers.js
// Pure, Firebase-free engine for the IMPACT "Know Your Numbers" worksheet.
//   computeKnowYourNumbersMetrics — live actuals from deals + leads.
//   deriveKnowYourNumbers        — every computed number on the sheet, from one
//                                  place, so the screen and the PDF always agree.
//   buildKnowYourNumbersValues   — final strings keyed for the PDF layout.
// Convention: a metric that can't be computed is null (never NaN); null prints
// as a blank on the sheet.
import { parseLocalDate, notDeleted, ownedBy } from "./statusSheetData";
import { incomeForAgent } from "./dealCredit";

const isClosedWon = (d) => String(d?.dealStage || "").toLowerCase() === "closed";
const closeDateOf = (d) => parseLocalDate(d?.closedDate || d?.closingdate);
const inMonth = (dt, year, month) =>
  !!dt && dt.getFullYear() === year && dt.getMonth() === month;

// Leads live in the pipelines: dated by createdAt (ms), owned via assignedAgent
// (the Lead Tracker's convention — deals use agentData instead).
const leadNotDeleted = (l) => !(Number(l?.deletedAt) > 0);
const leadOwnedBy = (l, agentId) =>
  !agentId || String(l?.assignedAgent || "") === String(agentId);

export function computeKnowYourNumbersMetrics(
  { deals, buyerLeads, listingLeads } = {},
  { agentId, asOf = new Date() } = {},
) {
  const dealList = Array.isArray(deals) ? deals : [];
  const yearStart = new Date(asOf.getFullYear(), 0, 1);
  const prev = new Date(asOf.getFullYear(), asOf.getMonth() - 1, 1);

  const closedYtd = dealList.filter((d) => {
    if (!isClosedWon(d) || !notDeleted(d)) return false;
    const dt = closeDateOf(d);
    return !!dt && dt >= yearStart && dt <= asOf;
  });

  // Units and volume: one deal, one owner. A sale counts once, for the person
  // credited with it, so two people on the same deal never make it two sales.
  const creditedYtd = closedYtd.filter((d) => ownedBy(d, agentId));
  const unitsYtd = creditedYtd.length;
  const volumeYtd = creditedYtd.reduce((s, d) => s + Number(d.offerPrice || 0), 0);

  // Income: dollars follow the money, not the unit. A referring agent is paid
  // his fee on a sale he is not credited with, and the team lead is paid his
  // own slice of that same sale, so both of them show up here and the two add
  // back up to exactly what the deal paid out. incomeForAgent is the one money
  // rule, shared with the IMPACT Scorecard so the two printed sheets can never
  // disagree; it returns 0 for anyone the deal never paid, so no extra
  // ownership filter is needed. The team view (no agentId) is unchanged: the
  // deal's gross commission, counted once.
  const incomeYtd = closedYtd.reduce((s, d) => s + incomeForAgent(d, agentId), 0);

  // The SAME money rule, but summed over ONLY the deals this person is credited
  // with. It exists for one job: to be the numerator of the commission-rate
  // line below, and nothing else. A rate is meaningless unless its top and its
  // bottom describe the same deals. volumeYtd is the sale price of the CREDITED
  // deals, so the dollars divided into it have to be the dollars earned ON
  // THOSE SAME DEALS. Dividing every dollar he was paid (which includes referral
  // fees earned on other people's sales, where no sale price of his own lands in
  // the denominator) by his own sale prices printed a wildly overstated
  // commission percentage. Henry prints this sheet and checks it against real
  // closing statements, so the two halves of this fraction must never come from
  // different sets of deals. The income LINE above deliberately stays on the
  // paid basis: it answers "what was I actually paid this year", which is the
  // right question for an income line.
  const creditedIncomeYtd = creditedYtd.reduce(
    (s, d) => s + incomeForAgent(d, agentId),
    0,
  );

  const closingsPerMonth = dealList.filter(
    (d) =>
      isClosedWon(d) &&
      notDeleted(d) &&
      ownedBy(d, agentId) &&
      inMonth(closeDateOf(d), prev.getFullYear(), prev.getMonth()),
  ).length;

  const allLeads = [
    ...(Array.isArray(buyerLeads) ? buyerLeads : []),
    ...(Array.isArray(listingLeads) ? listingLeads : []),
  ];
  const leadsPerMonth = allLeads.filter((l) => {
    if (!l || !leadNotDeleted(l) || !leadOwnedBy(l, agentId)) return false;
    const created = Number(l.createdAt) || 0;
    return created && inMonth(new Date(created), prev.getFullYear(), prev.getMonth());
  }).length;

  return {
    unitsYtd,
    volumeYtd,
    // Total dollars actually paid to this person this year, across every deal
    // that paid him. Paid basis on purpose. Do NOT use it as a rate numerator.
    incomeYtd,
    // Exposed so the rate below can be checked by hand, and so a future reader
    // can see at a glance that the two income figures are different on purpose.
    creditedIncomeYtd,
    // incomeYtd minus creditedIncomeYtd: the dollars this person was paid on
    // sales that belong to somebody else. It is the whole reason the income
    // line sits above a shorter deal list, so the sheet now says it out loud
    // instead of leaving a reader to work out why 1 closing at $7,666 sits
    // under $11,210.20 of income.
    otherAgentsIncomeYtd:
      Math.round((incomeYtd - creditedIncomeYtd) * 100) / 100,
    // Both halves come from creditedYtd, so a deal contributes its price and its
    // dollars or neither.
    avgSalesPrice: unitsYtd > 0 ? Math.round(volumeYtd / unitsYtd) : null,
    // A percent (2.5 = 2.5%), matching the sheet's "BPS or commission %" label.
    // Credited income over credited volume: same deals top and bottom.
    avgCommissionPct: volumeYtd > 0 ? (creditedIncomeYtd / volumeYtd) * 100 : null,
    leadsPerMonth,
    closingsPerMonth,
  };
}

// "$9,400,000" / "2.68%" / " 27 " → number; blank or garbage → null.
export function parseNum(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  const cleaned = String(value).replace(/[$,%\s]/g, "");
  if (cleaned === "" || cleaned === "-") return null;
  const n = Number(cleaned);
  return Number.isNaN(n) ? null : n;
}

const round1 = (n) => Math.round(n * 10) / 10;
const num = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : null);
const minus = (goal, today) =>
  num(goal) == null || num(today) == null ? null : goal - today;

// Whole months from asOf to the goal date (≥ 1 for any future date; null when
// missing or already past).
export function monthsUntil(goalDate, asOf = new Date()) {
  const goal = parseLocalDate(goalDate);
  if (!goal) return null;
  const days = (goal - asOf) / 86400000;
  if (days <= 0) return null;
  return Math.max(1, Math.round(days / 30.44));
}

export function deriveKnowYourNumbers(v = {}) {
  const goals = v.goals || {};
  const asOf = v.asOf || new Date();

  // Average sale price times the commission rate, less the per-file costs. Both
  // inputs are now built from the CREDITED deals, so this cancels down to
  // "dollars earned on my own closings ÷ my closings, minus costs" — a genuine
  // per-deal figure. It would not be one if the rate still carried referral fees
  // in its numerator, because those fees have no closing of his own underneath.
  const avgIncomePerClosing =
    num(v.avgSalesPrice) == null || num(v.avgCommissionPct) == null
      ? null
      : v.avgSalesPrice * (v.avgCommissionPct / 100) - (num(v.perFileCosts) || 0);

  const closingRatio =
    num(v.leadsPerMonth) > 0 && num(v.closingsPerMonth) != null
      ? Math.round((v.closingsPerMonth / v.leadsPerMonth) * 100) / 100
      : null;

  const totalHoursYtd =
    num(v.hoursPerWeek) == null || num(v.weeksWorkedYtd) == null
      ? null
      : v.hoursPerWeek * v.weeksWorkedYtd;

  // Paid basis on both halves, deliberately: every dollar he took home over
  // every hour he worked to get it. Referral fees are real income and the hours
  // behind them are real hours, so neither side is filtered to credited deals.
  const hourlyWage =
    totalHoursYtd > 0 && num(v.incomeYtd) != null
      ? Math.round(v.incomeYtd / totalHoursYtd)
      : null;

  const monthsToGoal = monthsUntil(v.goalDate, asOf);

  const gaps = {
    income: minus(goals.income, v.incomeYtd),
    volume: minus(goals.volume, v.volumeYtd),
    closings: minus(goals.closings, v.unitsYtd),
    leadsPerMonth: minus(goals.leadsPerMonth, v.leadsPerMonth),
    hoursPerWeek: minus(goals.hoursPerWeek, v.hoursPerWeek),
    teamSeats: minus(goals.teamSeats, v.seatsToday),
  };

  const gapClosingsPerMonth =
    gaps.closings == null || monthsToGoal == null
      ? null
      : round1(gaps.closings / monthsToGoal);

  const gapLeadsPerMonth =
    gapClosingsPerMonth == null || !closingRatio
      ? null
      : Math.ceil(gapClosingsPerMonth / closingRatio);

  return {
    avgIncomePerClosing,
    closingRatio,
    totalHoursYtd,
    hourlyWage,
    monthsToGoal,
    gaps,
    gapClosingsPerMonth,
    gapLeadsPerMonth,
  };
}

// ---- formatting (shared by the tab preview and the PDF) ----
// Money always carries cents. Rounding to whole dollars hid exactly the number
// this sheet has to prove: Henry's $11,210.20 is his own $7,666.00 closing plus
// a $3,544.20 share of Joe's sale, and those three round to $11,210, $7,666 and
// $3,544, which do not add up on paper. The printed cells have no width limit
// (see LAYOUT in knowYourNumbersPdf.js), so the two extra characters cannot
// truncate anything.
const MONEY_2DP = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
export const fmtMoney = (n) =>
  n == null
    ? ""
    : (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("en-US", MONEY_2DP);
const fmtInt = (n) => (n == null ? "" : Math.round(n).toLocaleString("en-US"));
const fmt1 = (n) => (n == null ? "" : String(round1(n)));
const fmtRatio = (n) => (n == null ? "" : n.toFixed(2));
// The sheet already prints its own "$" before the income blank.
const fmtBare = (n) =>
  n == null ? "" : Math.abs(n).toLocaleString("en-US", MONEY_2DP);

// ---- what each number is actually counting (plain English, one place) ----
//
// The sheet's own printed labels are artwork inside the coach's template PDF,
// so they say "Income YTD" and nothing more. On its own that line is honest but
// unreadable: it is EVERY dollar the person was paid, including his team lead
// share of sales that belong to other agents, while Units, Volume, Avg Sales
// Price, Avg Commission and Avg Income Per Closing all describe HIS OWN
// closings. A broker reading 1 closing, $7,666 per closing and $11,210.20 of
// income has no way to tell those are two different sets of deals.
//
// Nothing below changes a number. It gives every number a sentence saying whose
// deals it covers, so the sheet reads correctly the first time. The screen and
// the printed footnote both take their words from here so they can never drift.
export const KYN_BASIS_NOTES = {
  me: {
    unitsYtd: "Your own closings only. A sale counts once, for the agent it belongs to.",
    volumeYtd: "Sale price of your own closings only.",
    incomeYtd:
      "Every dollar paid to you this year, including your team lead share of other agents' closings.",
    avgSalesPrice: "Averaged over your own closings only.",
    avgCommissionPct:
      "What you earned on your own closings, over the sale price of those same closings.",
    avgIncomePerClosing:
      "Your own closings only, so it will not add up to Income YTD when other agents' deals also paid you.",
    leadsPerMonth: "Leads assigned to you last month.",
    closingsPerMonth: "Your own closings last month.",
    hourlyWage:
      "Every dollar paid to you this year, over every hour you worked for it.",
  },
  team: {
    unitsYtd: "Every closing on the team, counted once per deal.",
    volumeYtd: "Sale price of every closing on the team.",
    incomeYtd:
      "The team's total commission on every closing this year, counted once per deal.",
    avgSalesPrice: "Averaged over every closing on the team.",
    avgCommissionPct: "The team's commission over the team's sale volume.",
    avgIncomePerClosing: "Averaged over every closing on the team.",
    leadsPerMonth: "Every lead the team took in last month.",
    closingsPerMonth: "Every closing on the team last month.",
    hourlyWage: "Team commission over the hours you entered above.",
  },
};

// The note for one field. Unknown scope falls back to the personal wording,
// which is the stricter of the two.
export function kynBasisNote(field, scope = "me") {
  const set = KYN_BASIS_NOTES[scope === "team" ? "team" : "me"];
  return set[field] || "";
}

// ---- the two bases, in the words that sit beside a number's own label ----
//
// Every figure on both coaching sheets stands on one of exactly two bases, and
// the defect this round fixes is that neither sheet ever said which one:
//
//   earned   -> every dollar this person was actually paid, his team lead cut
//               of other agents' sales included. Know Your Numbers' Income YTD
//               and the Scorecard's Gross Income Last Month are on this basis.
//   credited -> this person's OWN closings. One deal, one owner, so a sale is
//               never counted twice. Units, Volume, Avg Sales Price, the
//               commission rate and Avg Income Per Closing are on this basis.
//
// The small print under each number already spells this out in a sentence. The
// words below go INTO the label itself, because a broker scanning a column of
// labels never reaches the small print, and the two numbers he compares by
// hand (Income YTD against Avg Income Per Closing) sit two sections apart.
//
// They live in one place, and both sheets import them, because the same figure
// has to read the same way on Know Your Numbers and on the IMPACT Scorecard.
// The printed worksheets cannot carry these words at all: their labels are
// artwork baked into the coaches' template PDFs.
export const BASIS_LABEL = {
  earned: { me: "(everything paid to you)", team: "(the team's commission)" },
  credited: { me: "(your own closings)", team: "(whole team)" },
};

// The parenthetical for one basis. Unknown basis returns "" so a label that has
// no business carrying one simply prints its plain name.
export function basisLabel(basis, scope = "me") {
  const set = BASIS_LABEL[basis];
  if (!set) return "";
  return scope === "team" ? set.team : set.me;
}

// Which basis each Know Your Numbers figure stands on. Income is the only
// earned-basis number on the sheet; everything else describes his own closings.
export const KYN_FIELD_BASIS = {
  unitsYtd: "credited",
  volumeYtd: "credited",
  incomeYtd: "earned",
  avgSalesPrice: "credited",
  avgCommissionPct: "credited",
  avgIncomePerClosing: "credited",
};

// The plain name of each labelled figure, matching the coach's printed sheet.
const KYN_FIELD_NAMES = {
  unitsYtd: "Units Closed YTD",
  volumeYtd: "Volume YTD",
  incomeYtd: "Income YTD",
  avgSalesPrice: "Avg Sales Price / Loan Amount",
  avgCommissionPct: "Avg Commission %",
  avgIncomePerClosing: "Avg Income Per Closing",
};

// The on-screen label for one figure: its printed name plus the basis it stands
// on. Unknown field returns "".
export function kynFieldLabel(field, scope = "me") {
  const name = KYN_FIELD_NAMES[field];
  if (!name) return "";
  const suffix = basisLabel(KYN_FIELD_BASIS[field], scope);
  return suffix ? `${name} ${suffix}` : name;
}

// One line for the printed worksheet, sized to the space under the first row of
// blanks. NOT DRAWN TODAY: the template's LAYOUT lives in knowYourNumbersPdf.js,
// which is the only file that can give a value a spot on the page, and the
// labels beside each blank are baked into public/know-your-numbers-template.pdf.
// The wording is kept here, and handed out by buildKnowYourNumbersValues, so
// switching it on is a one-line change over there:
//   incomeBasisNote: { page: 0, x: 46, y: 252, size: 6.5, maxWidth: 520 },
export const KYN_PRINT_BASIS_NOTE = {
  me: "Income YTD includes your share of other agents' closings. Units, Volume and Avg Income Per Closing are your own closings only.",
  team: "Income YTD is the team's commission on every closing, counted once per deal. Units and Volume cover every closing on the team.",
};

// The sentence that makes the income line reconcile out loud. Returns "" when
// there is nothing to explain (the team view, or a year in which nobody else's
// deal paid this person), so the screen shows it only when it is needed.
export function incomeSplitSentence(m = {}) {
  const total = num(m.incomeYtd);
  const own = num(m.creditedIncomeYtd);
  if (total == null || own == null) return "";
  const other =
    num(m.otherAgentsIncomeYtd) != null
      ? m.otherAgentsIncomeYtd
      : Math.round((total - own) * 100) / 100;
  if (!(other > 0)) return "";
  const units = Number(m.unitsYtd) || 0;
  const closings = `${units} ${units === 1 ? "closing" : "closings"}`;
  return (
    `Your ${fmtMoney(total)} is ${fmtMoney(own)} earned on your own ${closings}, ` +
    `plus ${fmtMoney(other)} earned on other agents' closings.`
  );
}

// ISO date inputs print the app's standard MM/DD/YYYY; anything else passes through.
export const fmtDateUS = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(s || "");
};

const str = (x) => (x == null ? "" : String(x));

// Flatten UI state + derived math into the strings the PDF layout draws.
// `numbers` are the display strings (auto or overridden) — printed verbatim.
export function buildKnowYourNumbersValues({
  name,
  date,
  numbers = {},
  typed = {},
  derived = {},
  scope = "me",
}) {
  const d = derived;
  const gaps = d.gaps || {};
  const tasks = typed.tasksBelowWage || [];
  const moves = typed.moves || [];
  const goals = typed.goals || {};
  const t = (i) => tasks[i] || {};
  const mv = (i) => moves[i] || {};
  const choice = typed.staffingChoice || "";

  return {
    name: str(name),
    date: str(date),

    // Carried so the printed sheet can say whose deals its numbers cover. It
    // draws nothing until knowYourNumbersPdf.js gives it a spot (see
    // KYN_PRINT_BASIS_NOTE above); unknown keys are ignored there, so shipping
    // it now is harmless and makes that a one-line change.
    incomeBasisNote:
      KYN_PRINT_BASIS_NOTE[scope === "team" ? "team" : "me"],

    unitsYtd: str(numbers.unitsYtd),
    volumeYtd: str(numbers.volumeYtd),
    incomeYtd: str(numbers.incomeYtd),

    avgSalesPrice: str(numbers.avgSalesPrice),
    avgCommissionPct: str(numbers.avgCommissionPct),
    perFileCosts: str(typed.perFileCosts),
    avgIncomePerClosing: fmtMoney(d.avgIncomePerClosing),

    leadsPerMonth: str(numbers.leadsPerMonth),
    closingsPerMonth: str(numbers.closingsPerMonth),
    closingRatio: fmtRatio(d.closingRatio),

    hoursPerWeek: str(typed.hoursPerWeek),
    weeksWorkedYtd: str(typed.weeksWorkedYtd),
    totalHoursYtd: fmtInt(d.totalHoursYtd),
    hourlyWage: fmtMoney(d.hourlyWage),

    task1: str(t(0).task), task1Hrs: str(t(0).hrs), task1Owner: str(t(0).owner),
    task2: str(t(1).task), task2Hrs: str(t(1).hrs), task2Owner: str(t(1).owner),
    task3: str(t(2).task), task3Hrs: str(t(2).hrs), task3Owner: str(t(2).owner),

    staffingNoneX: choice === "none" ? "X" : "",
    staffingDelegateX: choice === "delegate" ? "X" : "",
    staffingHireX: choice === "hire" ? "X" : "",
    staffingAdjustment: str(typed.staffingAdjustment),
    staffingBy: fmtDateUS(typed.staffingBy),

    goalDate: fmtDateUS(typed.goalDate),
    monthsToGoal: fmtInt(d.monthsToGoal),

    todayIncome: str(numbers.incomeYtd),
    goalIncome: str(goals.income),
    gapIncome: fmtMoney(gaps.income),
    todayVolume: str(numbers.volumeYtd),
    goalVolume: str(goals.volume),
    gapVolume: fmtMoney(gaps.volume),
    todayClosings: str(numbers.unitsYtd),
    goalClosings: str(goals.closings),
    gapClosings: fmtInt(gaps.closings),
    todayLeads: str(numbers.leadsPerMonth),
    goalLeads: str(goals.leadsPerMonth),
    gapLeadsMonthly: fmtInt(gaps.leadsPerMonth),
    todayHours: str(typed.hoursPerWeek),
    goalHours: str(goals.hoursPerWeek),
    gapHoursPerWeek: fmtInt(gaps.hoursPerWeek),
    todaySeats: str(typed.seatsToday),
    goalSeats: str(goals.teamSeats),
    gapTeamSeats: fmtInt(gaps.teamSeats),

    gapClosingsPerMonth: fmt1(d.gapClosingsPerMonth),
    gapLeadsPerMonth: fmtInt(d.gapLeadsPerMonth),
    myGapClosings: fmt1(d.gapClosingsPerMonth),
    myGapLeads: fmtInt(d.gapLeadsPerMonth),

    biggestGap1: str((typed.biggestGaps || [])[0]),
    biggestGap2: str((typed.biggestGaps || [])[1]),
    biggestGap3: str((typed.biggestGaps || [])[2]),

    move1: str(mv(0).move), move1Weekly: str(mv(0).weeklyNum), move1Starts: fmtDateUS(mv(0).starts),
    move2: str(mv(1).move), move2Weekly: str(mv(1).weeklyNum), move2Starts: fmtDateUS(mv(1).starts),
    move3: str(mv(2).move), move3Weekly: str(mv(2).weeklyNum), move3Starts: fmtDateUS(mv(2).starts),

    sentenceLeads: fmtInt(d.gapLeadsPerMonth),
    sentenceDeals: fmt1(d.gapClosingsPerMonth),
    sentenceIncome: fmtBare(parseNum(goals.income)),
  };
}
